import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { notify } from 'services';
import { RESTRICTED_PRODUCTS_ROUTES } from 'lib/routes';
import {
  useFetchScheduledRestrictedProductsDataImports,
} from 'hooks/api/scheduled-restricted-products-data-imports';
import {
  useUploadRestrictedProductFiles,
  useUploadSaleMovementsFile,
} from 'hooks/api/restricted-products';
import {
  useScheduledDataImportsSearchParams,
} from 'hooks/store/scheduled-restricted-products-data-imports/use-scheduled-data-imports-search-params';
import { Scaffold } from 'layouts';
import { DataTableV2 } from 'components/ui/DataTableV2';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';

import { useScheduledDataImportColumns } from './hooks';
import {
  UploadDataForm,
  type UploadDataPayload,
  ScheduledImportsFilter,
} from './components';
import { TabbedNavigation } from '../shared-components';

const NAVIGATE_TO_EVENT_LOGS_AFTER_UPLOAD = false;

export const ImportFilesPage = () => {
  const navigate = useNavigate();
  const { fetchScheduledImports } = useFetchScheduledRestrictedProductsDataImports();
  const { uploadBlockedProductsFile } = useUploadRestrictedProductFiles();
  const { uploadSaleMovementsFile } = useUploadSaleMovementsFile();
  const {
    filters,
    updateFilters,
    paginationControl,
    updatePaginationControl,
    clearState,
  } = useScheduledDataImportsSearchParams();

  const queryClient = useQueryClient();
  const columns = useScheduledDataImportColumns();

  const scheduledImportsQuery = useQuery({
    queryKey: ['fetch-scheduled-imports', filters, paginationControl],
    queryFn: () => fetchScheduledImports({
      filters,
      pagination: paginationControl,
    }),
    keepPreviousData: true,
    refetchInterval: 10_000,
  });

  const refetchScheduledImports = async () => {
    await queryClient.invalidateQueries(['fetch-scheduled-imports']);
  };

  const uploadRestrictedProductsMutation = useMutation({
    mutationFn: uploadBlockedProductsFile,
    onSuccess: async () => {
      await refetchScheduledImports();
      if (NAVIGATE_TO_EVENT_LOGS_AFTER_UPLOAD) {
        navigate(RESTRICTED_PRODUCTS_ROUTES.list().url);
      }
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
  });

  const uploadSaleMovementsMutation = useMutation({
    mutationFn: uploadSaleMovementsFile,
    onSuccess: async () => {
      await refetchScheduledImports();
      if (NAVIGATE_TO_EVENT_LOGS_AFTER_UPLOAD) {
        navigate(RESTRICTED_PRODUCTS_ROUTES.list().url);
      }
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
  });

  const onUploadDataFormSubmit = React.useCallback(
    (formData: UploadDataPayload) => {
      const uploadType = formData.uploadType;
      if (uploadType === 'blocked-products') {
        uploadRestrictedProductsMutation.mutate(formData);
      } else if (uploadType === 'sale-movements') {
        uploadSaleMovementsMutation.mutate(formData);
      }
    }, []);


  return (
    <Scaffold>
      <TabbedNavigation />
      <UploadDataForm
        onSubmit={onUploadDataFormSubmit}
        loading={
          uploadRestrictedProductsMutation.isLoading ||
          uploadSaleMovementsMutation.isLoading
        }
      />
      <h2 className={'font-GilroySemibold text-gray-600 mt-4'}>
        Fila de processamento de importações
      </h2>
      <PageHeaderForFilters
        headerTitle={<div className={'w-full font-GilroySemibold'}>Filtro</div>}
        className={'my-4'}
      >
        <ScheduledImportsFilter
          onSubmitFilters={updateFilters}
          onResetFilters={clearState}
        />
      </PageHeaderForFilters>
      <DataTableV2
        data={scheduledImportsQuery.data?.data ?? []}
        isLoading={scheduledImportsQuery.isLoading}
        columns={columns}
        manualPagination
        onPaginationChange={updatePaginationControl}
        pagination={{
          ...scheduledImportsQuery.data?.meta,
          currentPage: paginationControl.page,
          perPage: paginationControl.limit,
        }}
      />
    </Scaffold>
  );
};